import React, {
  Suspense,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth } from 'react-oidc-context';
import {
  prewarmModal,
  CFG_FIELDS,
  cfgSignature,
  errorKind,
  voiceLabel,
} from './ttsClient';
import useTTS from './useTTS';
import ModelStatus from './ModelStatus';
import AudioPlayBar from './AudioPlayBar';
import A11yStyles from './A11yStyles';
import ContactModal from './ContactModal';
import {
  StopIcon,
  PauseIcon,
  PlayArrowIcon,
  DownloadIcon,
  CloseIcon,
} from './icons';
import useMediaQuery from './useMediaQuery';
// import useSongSearch from './useSongSearch';
// import SongResults from './SongResults';
import { logDownload, logError, logRequest } from './requestLog';
import { HISTORY_ENABLED, clipTitle } from './historyClient';
import useHistory from './useHistory';
import { colors, type } from './theme';
import NowPlayingBar from './NowPlayingBar';
import ReaderCarousel from './ReaderCarousel';
import ShonaNavbar from './ShonaNavbar';
import { lazyPage } from '../utils/lazyPage';
import {
  READER_VOICES,
  speedsFor,
  initialVoice,
  rememberedSpeed,
  rememberVoice,
  rememberSpeed,
} from './readerVoice';

// Only signed-in readers with clips ever see this, so it stays out of the first
// download.
const HistorySection = lazyPage(() => import('./HistorySection'));

const enableDownload = true;

const isSingleWord = s => /^\S+$/.test(s.trim());

// ── audio imports for example cards ──────────────────────────────────────────
import john316Newslady from './John 3:16-newslady.wav';
import john316Sekuru from './John3:16-sekuru.wav';
import newsNewslady from './newsNewslady.wav';
import newsSekuru from './newsSekuru.wav';

// ── example passages ─────────────────────────────────────────────────────────
// One scripture (BibleDemo) + one real-world accessibility passage
// that showcase the breadth of the engine: faith and civic info.
// Each entry maps voices → prerecorded showcase WAVs so cards play instantly.
const EXAMPLES = [
  {
    id: 'john-3-16',
    reference: 'Johani 3:16',
    referenceEn: 'John 3:16 · Scripture',
    text: 'Nokuti Mwari vakada nyika nokudaro, vakapa Mwanakomana vavo mumwe oga, kuti ani naani anotenda maari arege kufa, asi ave nohupenyu husingaperi.',
    defaultVoice: 'sekuru',
    voices: { newslady: john316Newslady, sekuru: john316Sekuru },
  },
  {
    id: 'news',
    reference: 'Nhau Dzanhasi',
    referenceEn: "Today's News",
    text: 'Mangwanani ano kuHarare, hurumende yazivisa kuti zvikoro zvose zvichavhurwa nguva pfupi. Vabereki vanokurudzirwa kuti vanyoreswe vana vavo pasati pasvika svondo rinotevera.',
    voices: { newslady: newsNewslady, sekuru: newsSekuru },
  },
];

// ── voice toggle pill (shared by example cards) ──────────────────────────────
// TODO(voices): hidden for now — examples play the Woman voice only.
// Restore alongside the per-card voice state to re-enable switching.
/*
const VoiceToggle = ({ voice, onChange }) => (
  <div
    role="group"
    aria-label="Voice"
    style={{
      display: 'inline-flex',
      background: colors.sunken,
      border: `1px solid ${colors.border}`,
      borderRadius: 999,
      padding: 3,
      gap: 2,
    }}
  >
    {['newslady', 'sekuru'].map(v => (
      <button
        key={v}
        type="button"
        aria-pressed={voice === v}
        onClick={() => onChange(v)}
        style={{
          padding: '4px 12px',
          borderRadius: 999,
          border: 'none',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.5,
          cursor: 'pointer',
          textTransform: 'uppercase',
          background: voice === v ? colors.accent : 'transparent',
          color: voice === v ? colors.onAccent : colors.textDim,
          transition: 'background 0.15s, color 0.15s',
        }}
      >
        {v === 'newslady' ? 'Woman' : 'Man'}
      </button>
    ))}
  </div>
);
*/

// ── single example card ─────────────────────────────────────────────────────
// Playback is controlled by the parent: `active` says whether this card owns
// the page's single audio slot. Each card previously held its own independent
// `playing` state, so two examples could talk over each other and no single
// element could report what was actually audible.
const ExampleCard = ({ example, active, onPlay, onStop }) => {
  const audioRef = useRef(null);
  // TODO(voices): voice selector hidden — examples always play the Woman
  // (newslady) recording for now. Restore VoiceToggle + voice state to switch.
  const voice = 'newslady';

  // Follow the parent: start when handed the slot, rewind when it's taken away.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (active) {
      a.play().catch(() => onStop());
    } else if (!a.paused) {
      a.pause();
      a.currentTime = 0;
    }
  }, [active, onStop]);

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${active ? colors.borderActive : colors.border}`,
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'border-color 0.2s',
      }}
    >
      {/* header: reference + circular play/pause */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h3
            lang="sn"
            style={{
              ...type.sectionHeading,
              fontSize: 18,
              color: colors.text,
              margin: 0,
            }}
          >
            {example.reference}
          </h3>
          <div
            style={{
              color: colors.textDim,
              fontSize: 13,
              marginTop: 2,
            }}
          >
            {example.referenceEn}
          </div>
          {/* per-card echo of the now-playing state, so the indicator is
              visible without having to look at the fixed bar */}
          {active && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                color: colors.accent,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: colors.accent,
                  animation: 'shona-nowplaying-pulse 1.4s ease-in-out infinite',
                }}
              />
              Now playing · {voiceLabel(voice)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => (active ? onStop() : onPlay(example.id))}
          aria-label={active ? 'Stop' : `Play ${example.referenceEn}`}
          title={active ? 'Stop' : 'Play'}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: `1px solid ${active ? colors.accent : 'transparent'}`,
            background: active ? colors.accentBg : colors.accent,
            color: active ? colors.accent : colors.onAccent,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {active ? (
            <StopIcon fontSize="small" />
          ) : (
            <PlayArrowIcon fontSize="small" />
          )}
        </button>
      </div>

      {/* preview text */}
      <p
        lang="sn"
        style={{
          color: colors.text,
          fontSize: 15,
          lineHeight: 1.6,
          margin: 0,
          fontFamily: 'Georgia, "Times New Roman", serif',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {example.text}
      </p>

      {/* TODO(voices): voice toggle hidden — examples play the Woman voice only
      <div style={{ marginTop: 'auto' }}>
        <VoiceToggle voice={voice} onChange={setVoice} />
      </div>
      */}

      <audio
        ref={audioRef}
        src={example.voices[voice]}
        preload="none"
        onEnded={onStop}
      />
    </div>
  );
};

// ── examples section ─────────────────────────────────────────────────────────
// matches the 640px breakpoint used in A11yStyles
const MOBILE_QUERY = '(max-width: 640px)';

const ExamplesSection = ({ visible, activeId, onPlay, onStop }) => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const sectionRef = useRef(null);

  // `inert` removes the whole collapsed subtree from the tab order and the
  // a11y tree. Without it the section is only visually collapsed (maxHeight 0
  // + overflow hidden), so keyboard users could still tab into invisible play
  // buttons once the section was hidden. Set imperatively because React 18
  // does not forward the `inert` attribute for a boolean prop.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (visible) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [visible]);

  const card = ex => (
    <ExampleCard
      example={ex}
      active={activeId === ex.id}
      onPlay={onPlay}
      onStop={onStop}
    />
  );

  return (
    <section
      ref={sectionRef}
      aria-hidden={!visible}
      style={{
        maxHeight: visible ? 1400 : 0,
        opacity: visible ? 1 : 0,
        overflow: 'hidden',
        transition:
          'max-height 0.45s ease, opacity 0.3s ease, margin 0.3s ease',
        marginBottom: visible ? 24 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* The "Try your own text ↓" jump button lived here. It existed only
          because the textarea used to sit below the examples; the textarea is
          now first, so there is nothing to jump to. */}
      {/* Both list variants below are aria-labelledby this heading. */}
      <h2
        id="reader-examples-title"
        className="reader-examples-title"
        style={{
          ...type.eyebrow,
          color: colors.emphasis,
          margin: '0 0 14px',
        }}
      >
        Hear an example
      </h2>
      {isMobile ? (
        <ReaderCarousel
          items={EXAMPLES}
          getKey={ex => ex.id}
          renderItem={card}
          labelledBy="reader-examples-title"
          itemName="example"
        />
      ) : (
        <div
          role="list"
          aria-labelledby="reader-examples-title"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {EXAMPLES.map(ex => (
            <div key={ex.id} role="listitem">
              {card(ex)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ── reader (multiline) ──────────────────────────────────────────────────────
const ReaderBox = ({
  modelStatus,
  // Every generation request, when it is made.
  onSpoke,
  // A generation whose whole clip arrived; failed or stopped ones never call it.
  // Also says whether the clip came from the browser's cache.
  onGenerated,
  // A clip the visitor saved, with the generation it came from.
  onDownload,
  // Every failure the visitor is shown, with the generation it interrupted
  // plus the error's kind and technical detail.
  onError,
  onBeforeGenerate,
  charLimit,
  canSelectVoice,
  showAdvanced,
  onPlaybackChange,
  stopSignal,
  // Guests can generate and play but not download; see the download button.
  isSignedIn,
  // A signed-in reader's access token, sent so Modal saves the clip to History.
  historyToken,
}) => {
  const ready = modelStatus !== 'loading';
  const [text, setText] = useState('');
  // The visitor's last voice at the speed they last chose for it, or
  // DEFAULT_VOICE (YoungWoman) at its initial speed on a first visit; all
  // users can change it via the selector.
  const [voice, setVoice] = useState(initialVoice);
  const [speed, setSpeed] = useState(() => rememberedSpeed(voice));
  const [showSpeed, setShowSpeed] = useState(false);
  // Internal-only guidance knobs, keyed by API name. Kept as raw strings so an
  // empty field stays empty (and is left out of the request) instead of
  // collapsing to a number.
  const [cfgInputs, setCfgInputs] = useState(() =>
    Object.fromEntries(CFG_FIELDS.map(field => [field, '']))
  );
  const [showCfg, setShowCfg] = useState(false);
  const [lastGen, setLastGen] = useState({ voice: null, text: null });
  // The generation now loaded: what a download saves, and what a failure was
  // trying to make. A ref, not state, because useTTS can report a failure from
  // inside speak(), before anything set in that click has rendered.
  const generationRef = useRef(null);
  const {
    speak,
    pause,
    play,
    stop,
    download,
    wasCached,
    status,
    error,
    hasAudio,
    audioComplete,
    progress,
    seek,
  } = useTTS({
    onError: err =>
      onError?.({
        ...generationRef.current,
        kind: errorKind(err),
        detail: err?.message ?? String(err),
      }),
  });
  // const songs = useSongSearch();
  // WINNER //
  // Only the visitor's own picks are remembered. The single-word slowdown on
  // the textarea's blur is automatic, so it changes `speed` without these.
  const selectVoice = v => {
    setVoice(v);
    setSpeed(rememberedSpeed(v));
    rememberVoice(v);
  };
  const selectSpeed = s => {
    setSpeed(s);
    rememberSpeed(voice, s);
  };

  // Tell the page what this box is doing, so the now-playing bar can name it
  // and so starting an example can stop us (and vice versa).
  useEffect(() => {
    if (status === 'playing' || status === 'loading') {
      onPlaybackChange?.({
        kind: 'reader',
        state: status,
        label: 'Your text',
        voice,
      });
    } else {
      onPlaybackChange?.(null);
    }
  }, [status, voice, onPlaybackChange]);

  // The page bumps `stopSignal` when another source takes the audio slot.
  // Comparing against the last-seen value means a change in `stop`'s identity
  // can't trigger a spurious stop.
  const lastStopSignal = useRef(stopSignal);
  useEffect(() => {
    if (stopSignal === lastStopSignal.current) return;
    lastStopSignal.current = stopSignal;
    stop();
  }, [stopSignal, stop]);

  const { min: minSpeed, max: maxSpeed } = speedsFor(voice);

  // Blank (or not-yet-valid) fields are dropped, so they're never sent and the
  // backend keeps its default for them.
  const cfg = showAdvanced
    ? Object.fromEntries(
        CFG_FIELDS.map(field => [field, parseFloat(cfgInputs[field])]).filter(
          ([, value]) => Number.isFinite(value)
        )
      )
    : {};
  const cfgKey = cfgSignature(cfg);

  const isStale =
    lastGen.voice !== voice ||
    lastGen.text !== text ||
    lastGen.cfgKey !== cfgKey;

  const handleSpeak = () => {
    // Gate guests once they exhaust their free generations, and anyone whose
    // text is over their length limit. When blocked the parent shows the gate
    // and we skip generation.
    if (onBeforeGenerate && !onBeforeGenerate(text)) return;
    // If the button is in "Regenerate" state (audio already cached for this
    // exact text+voice), force a fresh API call instead of replaying cache.
    const force = hasAudio && !isStale;
    // Everything in here goes to the logs, so the history token stays out.
    const generation = { text, voice, speed, ...cfg };
    generationRef.current = generation;
    speak(text, { speed, voice, cfg, force, historyToken }).then(complete => {
      if (complete) onGenerated?.(generation, wasCached());
    });
    // songs.search(text); // discover related songs in parallel with audio
    setLastGen({ voice, text, cfgKey });
    onSpoke?.(generation);
  };

  const handlePlay = () => {
    if (isStale && text.trim()) {
      handleSpeak();
    } else {
      play();
    }
  };

  const busy = status === 'loading' || status === 'playing';
  const canStop =
    status === 'loading' || status === 'playing' || status === 'paused';
  const canPause = status === 'playing';
  const canPlay = hasAudio && status !== 'loading';
  const disabled = !ready;
  // Not `hasAudio`: that turns true on the first streamed chunk, long before
  // the rest has arrived, and a download then would have nothing to save.
  const canDownload = enableDownload && !disabled && audioComplete;
  const statusLabel =
    status === 'loading'
      ? 'Generating…'
      : status === 'playing'
        ? 'Playing…'
        : status === 'paused'
          ? 'Paused'
          : '';
  const speakButtonLabel =
    status === 'loading'
      ? 'Generating…'
      : status === 'playing'
        ? 'Playing…'
        : hasAudio && !isStale
          ? 'Regenerate'
          : 'Generate';

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: 'clamp(16px, 5vw, 24px)',
      }}
    >
      <h2
        id="reader-title"
        style={{
          ...type.sectionHeading,
          color: colors.text,
          marginBottom: 4,
          marginTop: 0,
        }}
      >
        Enter your text
      </h2>
      <ModelStatus state={modelStatus} />

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={() => {
          if (isSingleWord(text)) setSpeed(speedsFor(voice).singleWord);
        }}
        rows={10}
        disabled={disabled}
        lang="sn"
        autoCapitalize="sentences"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="done"
        aria-labelledby="reader-title"
        aria-describedby="reader-charcount"
        aria-busy={modelStatus === 'loading' || undefined}
        style={{
          width: '100%',
          background: colors.sunken,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          padding: 14,
          fontSize: 18,
          lineHeight: 1.7,
          fontFamily: 'Georgia, serif',
          resize: 'vertical',
          boxSizing: 'border-box',
          minHeight: 220,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
          marginTop: 4,
        }}
      />

      {/* Wraps rather than squeezing: at ~375px the count and the two controls
          don't fit on one line, and without this the count breaks mid-phrase
          ("0" / "chars"). */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span
          id="reader-charcount"
          aria-live="polite"
          style={{
            color: text.length > charLimit ? colors.danger : colors.textDim,
            fontSize: 14,
            whiteSpace: 'nowrap',
          }}
        >
          {formatCount(text.length)} / {formatCount(charLimit)} chars
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            marginLeft: 'auto',
          }}
        >
          <button
            type="button"
            disabled={disabled}
            onClick={() => setShowSpeed(s => !s)}
            aria-expanded={showSpeed}
            aria-controls="reader-speed-slider"
            style={{
              minHeight: 44,
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 16px',
              borderRadius: 999,
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: showSpeed ? colors.accent : colors.textDim,
              ...type.controlLabel,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'color 0.15s',
            }}
          >
            Speed {showSpeed ? '▴' : '▾'}
          </button>
          {/* Internal-only: cfg_spk / cfg_text guidance knobs. */}
          {showAdvanced && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setShowCfg(s => !s)}
              aria-expanded={showCfg}
              aria-controls="reader-cfg-panel"
              style={{
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0 16px',
                borderRadius: 999,
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: showCfg ? colors.accent : colors.textDim,
                ...type.controlLabel,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'color 0.15s',
              }}
            >
              CFG {showCfg ? '▴' : '▾'}
            </button>
          )}
          {/* Voice selector is available to all users; opens on their last
              pick, or YoungWoman on a first visit. */}
          {canSelectVoice && (
            <select
              aria-label="Voice"
              disabled={disabled}
              value={voice}
              onChange={e => selectVoice(e.target.value)}
              style={{
                minHeight: 44,
                background: colors.sunken,
                border: `1px solid ${colors.border}`,
                borderRadius: 999,
                padding: '0 16px',
                // 16px keeps iOS Safari from zoom-jumping the page on focus
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: 0.2,
                color: colors.text,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {READER_VOICES.map(v => (
                <option key={v} value={v}>
                  {voiceLabel(v)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {(hasAudio || status === 'loading') && (
        <AudioPlayBar
          current={progress.current}
          duration={progress.duration}
          buffering={progress.buffering}
          durationKnown={progress.durationKnown}
          chunksLoaded={progress.chunksLoaded}
          onSeek={seek}
          disabled={disabled}
        />
      )}

      {showSpeed && (
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: colors.sunken,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <label
              htmlFor="reader-speed-slider"
              style={{ ...type.controlLabel, color: colors.textDim }}
            >
              Speed
            </label>
            <span
              aria-live="polite"
              style={{
                color: colors.accent,
                fontSize: 14,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600,
              }}
            >
              {speed.toFixed(2)}×
            </span>
          </div>
          <input
            id="reader-speed-slider"
            type="range"
            min={minSpeed}
            max={maxSpeed}
            step="0.05"
            value={speed}
            onChange={e => selectSpeed(parseFloat(e.target.value))}
            disabled={disabled}
            style={{
              width: '100%',
              accentColor: colors.accent,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          />
        </div>
      )}

      {showAdvanced && showCfg && (
        <div
          id="reader-cfg-panel"
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: colors.sunken,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {CFG_FIELDS.map(field => (
            <div key={field} style={{ flex: '1 1 140px', minWidth: 140 }}>
              <label
                htmlFor={`reader-${field}`}
                style={{
                  display: 'block',
                  color: colors.textDim,
                  fontSize: 13,
                  letterSpacing: 0.5,
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {field}
              </label>
              <input
                id={`reader-${field}`}
                type="number"
                step="0.1"
                inputMode="decimal"
                placeholder="default"
                value={cfgInputs[field]}
                onChange={e =>
                  setCfgInputs(prev => ({ ...prev, [field]: e.target.value }))
                }
                disabled={disabled}
                style={{
                  width: '100%',
                  background: colors.sunken,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  // 16px keeps iOS Safari from zoom-jumping the page on focus
                  fontSize: 16,
                  boxSizing: 'border-box',
                  cursor: disabled ? 'not-allowed' : 'text',
                }}
              />
            </div>
          ))}
          <p
            style={{
              width: '100%',
              margin: 0,
              color: colors.textDim,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            Leave blank to use the model defaults — empty fields aren't sent to
            the API.
          </p>
        </div>
      )}

      <div
        className="reader-actions"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          gap: 10,
          // In the two-column layout the card can be ~400px wide, where the
          // status text plus four transport buttons and Generate only just
          // fit; wrap rather than spill past the card's padding.
          flexWrap: 'wrap',
        }}
      >
        <span
          role="status"
          aria-live="polite"
          style={{ color: colors.textDim, fontSize: 14 }}
        >
          {statusLabel}
        </span>
        <div
          className="reader-action-group"
          // marginLeft keeps the group right-aligned if it wraps to its own row
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginLeft: 'auto',
          }}
        >
          {/* Transport only exists once there is something to transport.
              These four buttons used to render permanently at opacity 0.5 —
              a row of identical dim circles, at most one of them usable. */}
          {(hasAudio || status === 'loading') &&
            (() => {
              const stopOn = !disabled && canStop;
              const pauseOn = !disabled && canPause;
              const playOn = !disabled && canPlay;
              const iconBtn = on => ({
                background: on ? colors.accent : 'transparent',
                color: on ? colors.onAccent : colors.accent,
                border: `1px solid ${on ? colors.accent : colors.border}`,
                width: 44,
                height: 44,
                padding: 0,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: on ? 'pointer' : 'not-allowed',
                opacity: on ? 1 : 0.5,
                transition: 'background 0.15s, color 0.15s',
              });
              return (
                <>
                  <button
                    type="button"
                    onClick={stop}
                    disabled={!stopOn}
                    aria-label="Stop"
                    title="Stop"
                    style={iconBtn(stopOn)}
                  >
                    <StopIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    onClick={pause}
                    disabled={!pauseOn}
                    aria-label="Pause"
                    title="Pause"
                    style={iconBtn(pauseOn)}
                  >
                    <PauseIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    onClick={handlePlay}
                    disabled={!playOn}
                    aria-label="Play"
                    title="Play"
                    style={iconBtn(playOn)}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </button>
                  {enableDownload &&
                    (isSignedIn ? (
                      <button
                        type="button"
                        onClick={() => {
                          const slug = text
                            .trim()
                            .replace(/[/\\:*?"<>|]/g, '')
                            .replace(/\s+/g, '-')
                            .slice(0, 60)
                            .replace(/[-.]+$/g, '');
                          // Taken now: the box may start a new generation
                          // while the clip is still being assembled.
                          const generation = generationRef.current;
                          download(`tsumo-${slug}.wav`).then(saved => {
                            if (saved) onDownload?.(generation);
                          });
                        }}
                        disabled={!canDownload}
                        aria-label="Download audio"
                        title="Download audio"
                        style={iconBtn(canDownload)}
                      >
                        <DownloadIcon fontSize="small" />
                      </button>
                    ) : (
                      // Guests see the control but can't use it. aria-disabled
                      // rather than `disabled` so it stays focusable: a
                      // disabled button can't take focus or reliably show a
                      // tooltip, leaving keyboard and touch users with no
                      // explanation. No onClick, so activating it is a no-op.
                      <span className="reader-tooltip-wrap">
                        <button
                          type="button"
                          aria-disabled="true"
                          aria-label="Download audio"
                          aria-describedby="reader-download-tip"
                          data-testid="reader-download-locked"
                          style={iconBtn(false)}
                        >
                          <DownloadIcon fontSize="small" />
                        </button>
                        <span
                          id="reader-download-tip"
                          role="tooltip"
                          className="reader-tooltip"
                        >
                          Sign in to download
                        </span>
                      </span>
                    ))}
                </>
              );
            })()}
          <button
            type="button"
            className="reader-generate"
            aria-describedby="reader-usage-policy"
            onClick={handleSpeak}
            disabled={disabled || busy || !text.trim()}
            style={{
              background: colors.accent,
              color: colors.onAccent,
              border: 'none',
              padding: '10px 22px',
              borderRadius: 999,
              fontSize: 16,
              fontWeight: 600,
              cursor:
                disabled || busy || !text.trim() ? 'not-allowed' : 'pointer',
              opacity: disabled || busy || !text.trim() ? 0.5 : 1,
            }}
          >
            {speakButtonLabel}
          </button>
        </div>
      </div>

      {/* {(songs.loading || songs.results.length > 0 || songs.error) && (
        <div style={{ marginTop: 24 }}>
          <h2
            style={{
              color: colors.accent,
              fontSize: 15,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              fontWeight: 700,
              margin: 0,
            }}
          >
            Discover
          </h2>
          <SongResults
            results={songs.results}
            loading={songs.loading}
            error={songs.error}
            source={songs.source}
          />
        </div>
      )} */}

      {error && (
        <div
          role="alert"
          style={{ color: colors.danger, marginTop: 12, fontSize: 15 }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// ── "Who uses Shona TTS?" pop-up ─────────────────────────────────────────────
// Opened from the navbar; this used to be a section at the foot of the page.
// A native <dialog> opened with showModal(), so the browser supplies the
// backdrop, Escape-to-close, the focus trap, inertness of the page behind it
// and focus return to the navbar button. It stays mounted while closed so the
// copy is still in the DOM for crawlers — a closed <dialog> just isn't drawn.
const UseCasesDialog = ({ open, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="reader-dialog"
      aria-labelledby="use-cases-title"
      data-testid="use-cases-dialog"
      // Fires for Escape as well as our own close(), keeping `open` in sync.
      onClose={onClose}
      // A click on the ::backdrop is reported on the <dialog> itself; clicks
      // inside the box land on the panel, which fills it edge to edge.
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="reader-dialog-panel">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 12,
          }}
        >
          <h2
            id="use-cases-title"
            style={{
              ...type.sectionHeading,
              color: colors.emphasis,
              margin: '6px 0 0',
            }}
          >
            Who uses Shona TTS?
          </h2>
          <button
            type="button"
            className="reader-dialog-close"
            aria-label="Close"
            title="Close"
            data-testid="use-cases-dialog-close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <p
          style={{
            color: colors.textDim,
            fontSize: 16,
            lineHeight: 1.65,
            margin: 0,
          }}
        >
          Dzose Shona Reader is a natural-sounding{' '}
          <strong style={{ color: colors.text }}>Shona text to speech</strong>{' '}
          (TTS) tool. Paste any ChiShona passage, scripture, news, classroom
          material, or enterprise script and the model generates clear Shona
          voice audio on demand.
        </p>
      </div>
    </dialog>
  );
};

// ── page layout ──────────────────────────────────────────────────────────────
// From TWO_COLUMN_QUERY up the page splits in half: headline, examples and the
// usage policy on the left, the reader on the right. Below it everything
// stacks with the reader straight after the headline, so the input stays above
// the fold on a phone and the policy stays right under Generate.
//
// The reader spans all three left-hand rows; the last row is 1fr so whichever
// column is taller sets the height and the shorter one just sits at the top.
//
// Spacing is margins rather than row-gap: the examples collapse to zero height
// after a generation on narrow screens, and a gap would leave a hole behind.
const PAGE_MAX_WIDTH = 1200;
const TWO_COLUMN_QUERY = '(min-width: 1024px)';

const READER_LAYOUT_CSS = `
  .shona-page .reader-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'intro' 'reader' 'policy' 'examples';
  }
  .shona-page .reader-intro {
    grid-area: intro;
    text-align: center;
    margin-bottom: clamp(28px, 7vw, 48px);
  }
  .shona-page .reader-headline {
    color: ${colors.emphasis};
    font-size: clamp(26px, 4vw, 44px);
    font-weight: 650;
    line-height: 1.15;
    letter-spacing: -0.02em;
    max-width: 640px;
    margin: 24px auto 0;
    text-wrap: balance;
  }
  .shona-page .reader-main { grid-area: reader; }
  .shona-page .reader-examples {
    grid-area: examples;
    margin-top: clamp(28px, 7vw, 48px);
  }
  .shona-page .reader-examples-title { text-align: center; }
  .shona-page .reader-policy {
    grid-area: policy;
    text-align: center;
    max-width: 600px;
    margin: 12px auto 0;
  }

  @media ${TWO_COLUMN_QUERY} {
    .shona-page .reader-layout {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-template-rows: auto auto 1fr;
      grid-template-areas:
        'intro reader'
        'examples reader'
        'policy reader';
      column-gap: clamp(40px, 5vw, 64px);
      align-items: start;
    }
    .shona-page .reader-intro {
      text-align: left;
      margin-bottom: 32px;
    }
    .shona-page .reader-headline { margin: 8px 0 0; }
    .shona-page .reader-examples { margin-top: 0; }
    .shona-page .reader-examples-title { text-align: left; }
    .shona-page .reader-policy {
      text-align: left;
      max-width: none;
      margin: 0;
    }
  }

  /* margin: auto is what centres a modal <dialog>; the UA sheet sets it, but
     the app's global reset zeroes it. */
  .shona-page .reader-dialog {
    margin: auto;
    width: min(560px, calc(100vw - 32px));
    max-height: calc(100dvh - 48px);
    padding: 0;
    border: 1px solid ${colors.border};
    border-radius: 16px;
    background: ${colors.card};
    color: ${colors.text};
    box-shadow: ${colors.shadow};
  }
  .shona-page .reader-dialog[open] {
    animation: reader-dialog-in 0.18s ease-out;
  }
  /* ::backdrop only inherits custom properties in recent browsers, hence the
     fallback scrim. */
  .shona-page .reader-dialog::backdrop {
    background: var(--sn-scrim, rgba(0, 0, 0, 0.5));
  }
  .shona-page .reader-dialog-panel {
    padding: clamp(20px, 5vw, 32px);
  }
  .shona-page .reader-dialog-close {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid ${colors.border};
    background: transparent;
    color: ${colors.textDim};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.15s, background 0.15s;
  }
  .shona-page .reader-dialog-close:hover {
    color: ${colors.accent};
    background: ${colors.accentBg};
  }
  /* Movement only, no fade: if the animation clock stalls (background tabs,
     some embedded webviews) the dialog must still be fully visible. */
  /* Hover/focus tooltip for the guest-locked download button. */
  .shona-page .reader-tooltip-wrap {
    position: relative;
    display: inline-flex;
  }
  .shona-page .reader-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    padding: 6px 10px;
    border-radius: 8px;
    background: ${colors.emphasis};
    color: ${colors.bg};
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    /* index.css sets * { max-width: 100% } at <=768px, which would clamp the
       tooltip to its 44px button and spill the text out of its background. */
    max-width: none;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.12s, visibility 0.12s;
  }
  .shona-page .reader-tooltip-wrap:hover .reader-tooltip,
  .shona-page .reader-tooltip-wrap:focus-within .reader-tooltip {
    opacity: 1;
    visibility: visible;
  }
  /* A11yStyles gives enabled buttons a press-scale; an aria-disabled one is
     still "enabled" to CSS, so cancel it — nothing happens on press. */
  .shona-page button[aria-disabled='true']:active {
    transform: none;
  }

  @keyframes reader-dialog-in {
    from { transform: translateY(8px) scale(0.98); }
    to { transform: none; }
  }
`;

// ── page shell ───────────────────────────────────────────────────────────────
// `nowPlaying` widens the bottom padding and shifts the mobile sticky Generate
// button up, so neither is covered by the fixed now-playing bar.
const PageShell = ({ children, nowPlaying, navbar }) => (
  <div
    className={`shona-page${nowPlaying ? ' has-nowplaying' : ''}`}
    style={{
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
    }}
  >
    <A11yStyles />
    <style>{READER_LAYOUT_CSS}</style>
    {navbar}
    <main
      style={{
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        padding: `clamp(24px, 6vw, 48px) clamp(16px, 5vw, 24px) calc(${
          nowPlaying ? 168 : 96
        }px + env(safe-area-inset-bottom))`,
      }}
    >
      {children}
    </main>
  </div>
);

// ── guest usage gating ───────────────────────────────────────────────────────
// Guests may generate this many clips before they have to sign up. Signed-in
// users get unlimited generations.
const FREE_GENERATION_LIMIT = 5;
const GUEST_COUNT_KEY = 'shonaReaderGuestGenerations';

// Longest text one generation may submit. A guest gets enough to judge the
// voice; an account raises it. The signed-in ceiling keeps a request well
// inside the Modal endpoint's 10-minute per-request timeout.
const GUEST_CHAR_LIMIT = 1000;
const USER_CHAR_LIMIT = 5000;

const formatCount = n => n.toLocaleString('en-US');

// Cognito hosted UI sign-out URL for the Shona Reader user pool.
const READER_COGNITO_DOMAIN =
  'https://dzose-shona-reader.auth.eu-west-1.amazoncognito.com';
const READER_CLIENT_ID = '1qc368j9l2rkh340ks88t788s5';

// Only this account sees the cfg_spk / cfg_text controls.
const ADVANCED_CONTROLS_EMAIL = 'janhivictor4@gmail.com';

const readGuestCount = () => {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(GUEST_COUNT_KEY);
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

// ── gate modal ───────────────────────────────────────────────────────────────
// What the gate says, keyed by why it opened. Only the guest reasons offer
// sign-up; a signed-in user over the length limit just needs shorter text.
const GATE_COPY = {
  generations: {
    eyebrow: 'Free limit reached',
    title: 'Sign up to keep listening',
    body: `You've used your ${FREE_GENERATION_LIMIT} free generations. Create a free account to keep turning ChiShona text into natural speech - no limits.`,
    offerSignUp: true,
  },
  guestLength: {
    eyebrow: 'Text too long',
    title: 'Sign up to submit longer texts',
    body: `Guests can submit up to ${formatCount(GUEST_CHAR_LIMIT)} characters at a time. Create a free account to submit up to ${formatCount(USER_CHAR_LIMIT)}.`,
    offerSignUp: true,
  },
  userLength: {
    eyebrow: 'Text too long',
    title: `Your text is above the ${formatCount(USER_CHAR_LIMIT)} character limit`,
    body: 'Please submit shorter text.',
    offerSignUp: false,
  },
};

const AuthGateModal = ({ reason, onSignIn, onClose }) => {
  const { eyebrow, title, body, offerSignUp } = GATE_COPY[reason];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-gate-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: colors.scrim,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 16,
          padding: 'clamp(24px, 6vw, 36px)',
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            ...type.eyebrow,
            color: colors.emphasis,
            marginBottom: 12,
          }}
        >
          {eyebrow}
        </div>
        <h2
          id="auth-gate-title"
          style={{
            color: colors.emphasis,
            fontSize: 'clamp(20px, 5vw, 26px)',
            fontWeight: 700,
            margin: '0 0 12px',
            lineHeight: 1.25,
          }}
        >
          {title}
        </h2>
        <p
          style={{
            color: colors.textDim,
            fontSize: 16,
            lineHeight: 1.6,
            margin: '0 0 24px',
          }}
        >
          {body}
        </p>
        <button
          type="button"
          onClick={offerSignUp ? onSignIn : onClose}
          style={{
            width: '100%',
            padding: '14px 22px',
            borderRadius: 999,
            border: 'none',
            background: colors.accent,
            color: colors.onAccent,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 0.4,
            cursor: 'pointer',
          }}
        >
          {offerSignUp ? "Sign up / Sign in - it's free" : 'OK'}
        </button>
        {offerSignUp && (
          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: 12,
              background: 'transparent',
              border: 'none',
              color: colors.textDim,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Maybe later
          </button>
        )}
      </div>
    </div>
  );
};

// Who and where, attached to everything the reader logs for /analytics.
const logContext = auth => ({
  userId: auth.user?.profile?.sub ?? null,
  email: auth.user?.profile?.email ?? null,
  page: '/shonareader',
});

const ShonaReader = () => {
  const auth = useAuth();
  const [modelStatus, setModelStatus] = useState('loading');
  const [hasSpoken, setHasSpoken] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showUseCases, setShowUseCases] = useState(false);
  // Side by side there is room for the examples next to the reader, so they
  // stay put after a generation instead of collapsing to make space.
  const isTwoColumn = useMediaQuery(TWO_COLUMN_QUERY);
  // Why the gate modal is open (a GATE_COPY key), or null when it's closed.
  const [gateReason, setGateReason] = useState(null);
  const [guestCount, setGuestCount] = useState(() => readGuestCount());
  const prewarmedRef = useRef(false);

  // ── single audio slot ──────────────────────────────────────────────────────
  // The page owns "what is currently playing" so the indicator has exactly one
  // thing to report. `activeClipId` names the playing example card or History
  // clip (null when the reader owns the slot); `clipPaused` says a History clip
  // is holding it; `readerPlayback` is what ReaderBox reports about itself;
  // `readerStop` is a counter ReaderBox watches to know it's been pre-empted.
  const [activeClipId, setActiveClipId] = useState(null);
  const [clipPaused, setClipPaused] = useState(false);
  const [readerPlayback, setReaderPlayback] = useState(null);
  const [readerStop, setReaderStop] = useState(0);
  const history = useHistory(auth, activeClipId);
  const { refreshAfterSave } = history;

  // Starting a clip pre-empts the reader and any other clip.
  const playClip = useCallback(id => {
    setReaderStop(n => n + 1);
    setClipPaused(false);
    setActiveClipId(id);
  }, []);

  const stopClip = useCallback(() => setActiveClipId(null), []);

  // ...and the reader starting pre-empts any clip.
  const handlePlaybackChange = useCallback(playback => {
    setReaderPlayback(playback);
    if (playback) setActiveClipId(null);
  }, []);

  const activeExample = EXAMPLES.find(ex => ex.id === activeClipId) ?? null;
  const activeHistoryClip =
    history.clips.find(clip => clip.clipId === activeClipId) ?? null;

  let nowPlaying = readerPlayback;
  if (activeExample) {
    nowPlaying = {
      state: 'playing',
      label: activeExample.referenceEn,
      voice: 'newslady',
    };
  } else if (activeHistoryClip) {
    nowPlaying = {
      state: clipPaused ? 'paused' : 'playing',
      label: clipTitle(activeHistoryClip.text),
      voice: activeHistoryClip.voice,
    };
  }

  const stopEverything = useCallback(() => {
    setActiveClipId(null);
    setReaderStop(n => n + 1);
  }, []);

  const isAuthenticated = auth.isAuthenticated;

  // Voice selection (including the Tony voice) is available to all users.
  const canSelectVoice = true;

  const showAdvanced =
    isAuthenticated &&
    (auth.user?.profile?.email ?? '').trim().toLowerCase() ===
      ADVANCED_CONTROLS_EMAIL;

  const handleSignIn = useCallback(() => {
    auth.signinRedirect();
  }, [auth]);

  const handleSignOut = useCallback(() => {
    const redirect =
      typeof window !== 'undefined'
        ? `${window.location.origin}/shonareader`
        : 'https://dzose.com/shonareader';
    const logoutUrl = `${READER_COGNITO_DOMAIN}/logout?client_id=${READER_CLIENT_ID}&logout_uri=${encodeURIComponent(
      redirect
    )}`;
    auth
      .removeUser()
      .catch(() => {})
      .finally(() => {
        window.location.href = logoutUrl;
      });
  }, [auth]);

  const charLimit = isAuthenticated ? USER_CHAR_LIMIT : GUEST_CHAR_LIMIT;

  // Gate check run right before every generation. Returns false (and shows
  // the gate modal) when a guest has used up their free generations, or when
  // the text is over the limit for this user - guests are offered sign-up,
  // signed-in users are asked to shorten the text.
  const handleBeforeGenerate = useCallback(
    text => {
      if (!isAuthenticated && readGuestCount() >= FREE_GENERATION_LIMIT) {
        setGateReason('generations');
        return false;
      }
      if (text.length > charLimit) {
        setGateReason(isAuthenticated ? 'userLength' : 'guestLength');
        return false;
      }
      return true;
    },
    [isAuthenticated, charLimit]
  );

  // Log every generation request — failures included, so analytics still sees
  // them — and keep the examples hidden. `meta` is { text, voice, speed } from
  // the ReaderBox generation.
  const handleSpoke = useCallback(
    meta => {
      setHasSpoken(true);
      logRequest({ ...meta, ...logContext(auth) });
    },
    [auth]
  );

  // Saved clips and shown failures each get their own table on /analytics.
  const handleDownload = useCallback(
    meta => logDownload({ ...meta, ...logContext(auth) }),
    [auth]
  );
  const handleError = useCallback(
    meta => logError({ ...meta, ...logContext(auth) }),
    [auth]
  );

  // Modal has finished saving by the time a clip completes, so a new clip is
  // already listed; one that isn't means the save failed, which the reader
  // never hears about from Modal. A clip from the browser's cache never went
  // to Modal, so nothing new is expected.
  const confirmSaved = useCallback(
    async generation => {
      if ((await refreshAfterSave()) === false) {
        logError({
          ...generation,
          ...logContext(auth),
          kind: 'historySave',
          detail: 'Clip finished but is not in History',
        });
      }
    },
    [auth, refreshAfterSave]
  );

  // Count a guest generation once its whole clip has arrived. Counting the
  // click instead charged guests for requests that failed or were stopped.
  const handleGenerated = useCallback(
    (generation, cached) => {
      if (isAuthenticated) {
        if (HISTORY_ENABLED && !cached) confirmSaved(generation);
        return;
      }
      const next = readGuestCount() + 1;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(GUEST_COUNT_KEY, String(next));
      }
      setGuestCount(next);
    },
    [isAuthenticated, confirmSaved]
  );

  const handleHistoryDownload = useCallback(
    clip =>
      handleDownload({
        text: clip.text,
        voice: clip.voice,
        speed: clip.speed,
      }),
    [handleDownload]
  );

  const { remove: removeClip, refresh: refreshHistory } = history;

  const handleDeleteClip = useCallback(
    async clipId => {
      await removeClip(clipId);
      setActiveClipId(active => (active === clipId ? null : active));
    },
    [removeClip]
  );

  // Most likely a link that went stale, so the next tap needs a fresh one.
  const handlePlaybackError = useCallback(() => {
    setActiveClipId(null);
    refreshHistory().catch(() => {});
  }, [refreshHistory]);

  useEffect(() => {
    if (prewarmedRef.current) return;
    prewarmedRef.current = true;
    document.title = 'Shona Text to Speech — Natural ChiShona Voice AI · Dzose';
    prewarmModal().finally(() => {
      setModelStatus('ready');
      setTimeout(() => setModelStatus('hidden'), 3000);
    });
  }, []);

  const remaining = Math.max(0, FREE_GENERATION_LIMIT - guestCount);

  const closeUseCases = useCallback(() => setShowUseCases(false), []);

  return (
    <PageShell
      nowPlaying={nowPlaying}
      navbar={
        <ShonaNavbar
          auth={auth}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          maxWidth={PAGE_MAX_WIDTH}
          menuItems={[
            {
              id: 'use-cases',
              label: 'Who uses it?',
              onClick: () => setShowUseCases(true),
              hasPopup: 'dialog',
            },
            {
              id: 'contact',
              label: 'Contact us',
              onClick: () => setShowContact(true),
              hasPopup: 'dialog',
            },
          ]}
        />
      }
    >
      {/* Grid areas, not source order, decide where each block lands — see
          READER_LAYOUT_CSS. */}
      <div className="reader-layout">
        {/* The "Dzose" eyebrow and the right-aligned auth row that used to sit
            here both moved into ShonaNavbar. */}
        <header className="reader-intro">
          <h1 className="reader-headline">
            Create and hear it in a natural Shona voice.
          </h1>
        </header>

        {/* On a phone the input comes straight after the headline: it is what
            the page is for. Examples follow as an alternative, not as a
            preamble to scroll past. */}
        <section
          id="try-your-own"
          className="reader-main"
          style={{ scrollMarginTop: 24 }}
        >
          <ReaderBox
            modelStatus={modelStatus}
            onSpoke={handleSpoke}
            onGenerated={handleGenerated}
            onDownload={handleDownload}
            onError={handleError}
            onBeforeGenerate={handleBeforeGenerate}
            charLimit={charLimit}
            canSelectVoice={canSelectVoice}
            showAdvanced={showAdvanced}
            onPlaybackChange={handlePlaybackChange}
            stopSignal={readerStop}
            isSignedIn={isAuthenticated}
            historyToken={HISTORY_ENABLED ? auth.user?.access_token : undefined}
          />
          {!isAuthenticated && !auth.isLoading && (
            <p
              style={{
                textAlign: 'center',
                color: colors.textDim,
                fontSize: 13,
                marginTop: 12,
              }}
            >
              {remaining > 0 ? (
                <>
                  {remaining} free generation{remaining === 1 ? '' : 's'} left ·{' '}
                  <button
                    type="button"
                    onClick={handleSignIn}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.accent,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Sign up for unlimited
                  </button>
                </>
              ) : (
                <>
                  Free limit reached ·{' '}
                  <button
                    type="button"
                    onClick={handleSignIn}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.accent,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Sign up to continue
                  </button>
                </>
              )}
            </p>
          )}
        </section>

        <div className="reader-examples">
          {history.clips.length > 0 ? (
            <Suspense fallback={null}>
              <HistorySection
                clips={history.clips}
                hasMore={history.hasMore}
                retentionDays={history.retentionDays}
                activeClipId={activeClipId}
                onPlay={playClip}
                onStop={stopClip}
                onPausedChange={setClipPaused}
                onDownload={handleHistoryDownload}
                onDelete={handleDeleteClip}
                onPlaybackError={handlePlaybackError}
                onShowMore={history.showMore}
              />
            </Suspense>
          ) : (
            <ExamplesSection
              visible={isTwoColumn || !hasSpoken}
              activeId={activeClipId}
              onPlay={playClip}
              onStop={stopClip}
            />
          )}
        </div>

        {/* Shown to everyone, signed in or not, so any account suspension or
            domain block has a stated basis: under Generate on a phone, under
            the examples beside the reader on desktop. It names categories of
            content rather than any group or belief, and the discretion clause
            covers the rest. */}
        <p
          id="reader-usage-policy"
          className="reader-policy"
          style={{
            color: colors.textDim,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          By generating audio you agree to use Dzose Shona Reader acceptably: no
          unlawful, hateful, harassing or deceptive content. We may suspend
          accounts or restrict access at our discretion, including for content
          we choose not to support. See our{' '}
          <a href="/privacy" style={{ color: colors.accent }}>
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="/terms" style={{ color: colors.accent }}>
            Terms
          </a>
          .
        </p>
      </div>

      <UseCasesDialog open={showUseCases} onClose={closeUseCases} />

      {showContact && <ContactModal onClose={() => setShowContact(false)} />}

      {gateReason && (
        <AuthGateModal
          reason={gateReason}
          onSignIn={handleSignIn}
          onClose={() => setGateReason(null)}
        />
      )}

      <NowPlayingBar playing={nowPlaying} onStop={stopEverything} />
    </PageShell>
  );
};

export default ShonaReader;
